import React, { useEffect, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import './AdminHeader.css';
import './AdminLayout.css';
import { FaBars, FaTimes } from 'react-icons/fa';
import { motion, AnimatePresence } from 'framer-motion';
import { io } from 'socket.io-client';

function AdminHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [pendingCount, setPendingCount] = useState(0);
  const [lowStockCount, setLowStockCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [inventoryToasts, setInventoryToasts] = useState([]);

  // toggle a body class so other page elements can respond when the admin mobile menu is open
  useEffect(() => {
    try {
      if (mobileMenuOpen) document.body.classList.add('admin-menu-open');
      else document.body.classList.remove('admin-menu-open');
    } catch (e) {
      // ignore (server-side rendering or restricted environment)
    }
    return () => { try { document.body.classList.remove('admin-menu-open'); } catch (e) {} };
  }, [mobileMenuOpen]);

  const fetchCounts = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return; // Don't fetch if no token
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API}/api/orders/summary`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const data = await res.json();
        // data: { counts: {...}, lowStockCount }
        // Count actionable orders: Pending + Awaiting Payment + Payment Confirmed
        const pending = data.counts?.Pending || 0;
        const awaiting = data.counts?.['Awaiting Payment'] || 0;
        const confirmed = data.counts?.['Payment Confirmed'] || 0;
        const actionableCount = pending + awaiting + confirmed;
        console.log('Orders badge breakdown:', { pending, awaiting, confirmed, total: actionableCount });
        setPendingCount(actionableCount);
        setLowStockCount(data.lowStockCount || 0);
      }
    } catch {}
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // Don't run if no token
    
    let alive = true;
    (async () => { if (alive) await fetchCounts(); })();
    const id = setInterval(fetchCounts, 30000);
    return () => { alive = false; clearInterval(id); };
  }, [fetchCounts]);

  // Realtime: update counts + inventory toasts
  useEffect(() => {
    const token = localStorage.getItem('token');
    let socket;
    try {
      socket = io(process.env.REACT_APP_API, { auth: { token } });
      socket.on('admin:summary', () => { try { fetchCounts(); } catch {} });
      socket.on('inventory:change', payload => {
        try { console.log('[socket] inventory:change received', payload); } catch {}
        if (!payload || !payload.productId) return;
        const id = `${payload.productId}-${Date.now()}`;
        setInventoryToasts(prev => {
          const next = [...prev, { id, payload }];
          return next.slice(-5);
        });
        setTimeout(() => {
          setInventoryToasts(prev => prev.filter(t => t.id !== id));
        }, 5000);
      });
    } catch {}
    return () => { try { socket && socket.disconnect(); } catch {} };
  }, [fetchCounts]);

  // Determine if current user is super admin (first use localStorage role, then fallback test request)
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return; // Don't check if no token
    
    const role = localStorage.getItem('role');
    if (role === 'superadmin') {
      setIsSuperAdmin(true);
      return;
    }
    // Fallback probe: attempt superadmin-only endpoint
    (async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_API}/api/admin/users/admins`, { headers: { Authorization: `Bearer ${token}` } });
        if (res.status === 200) setIsSuperAdmin(true);
      } catch {}
    })();
  }, []);

  const handleLogout = () => {
    // Clear all auth data
    localStorage.removeItem('token');
    localStorage.removeItem('isAdmin');
    localStorage.removeItem('role');
    
    // Use replace to prevent back button navigation
    navigate('/login', { replace: true });
    
    // Clear browser cache
    if (window.history && window.history.pushState) {
      window.history.pushState(null, '', window.location.href);
    }
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
    <header className="admin-header">
      <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
        {mobileMenuOpen ? <FaTimes /> : <FaBars />}
      </button>

      <div className="logo">
        <Link to="/admin" style={{ display: 'flex', alignItems: 'center', gap: '8px', textDecoration: 'none' }}>
          <img src="/images/chiqui_logo.svg" alt="Chiqui's Garden Logo" style={{ width: '32px', height: '32px', flexShrink: 0 }} />
          <span className="logo-text" style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>Chiqui's Garden Admin</span>
        </Link>
      </div>

      <AnimatePresence mode="wait">
        {mobileMenuOpen && (
          <motion.nav 
            className="admin-nav mobile-open"
            initial={{ opacity: 0, y: -10 }}
            animate={{ 
              opacity: 1, 
              y: 0,
              transition: { 
                duration: 0.25,
                ease: [0.4, 0, 0.2, 1]
              }
            }}
            exit={{ 
              opacity: 0, 
              y: -10,
              transition: { 
                duration: 0.2,
                ease: [0.4, 0, 0.2, 1]
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.05 }}
            >
              <Link to="/admin/orders" className={`orders-link ${isActive('/admin/orders') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>Orders{pendingCount > 0 && <span className="orders-badge">{pendingCount}</span>}</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.08 }}
            >
              <Link to="/admin/products" className={`products-link ${isActive('/admin/products') ? 'active' : ''}`} onClick={() => setMobileMenuOpen(false)}>Product Management{lowStockCount > 0 && <span className="products-badge">{lowStockCount}</span>}</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.11 }}
            >
              <Link to="/admin/customers" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/customers') ? 'active' : ''}>Customers</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.14 }}
            >
              <Link to="/admin/customer-concerns" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/customer-concerns') ? 'active' : ''}>Customer Concerns</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.17 }}
            >
              <Link to="/admin/promos" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/promos') ? 'active' : ''}>Promo Codes</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.20 }}
            >
              <Link to="/admin/rewards" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/rewards') ? 'active' : ''}>Rewards Program</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.23 }}
            >
              <Link to="/admin/notifications" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/notifications') ? 'active' : ''}>Notifications</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.26 }}
            >
              <Link to="/admin/delivery" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/delivery') ? 'active' : ''}>Delivery Fees</Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.29 }}
            >
              <Link to="/admin/reports" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/reports') ? 'active' : ''}>Reports</Link>
            </motion.div>
            {isSuperAdmin && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.2, delay: 0.32 }}
              >
                <Link to="/admin/super" onClick={() => setMobileMenuOpen(false)} className={isActive('/admin/super') ? 'active' : ''}>Super Admin</Link>
              </motion.div>
            )}
            <motion.div
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.2, delay: 0.35 }}
              className="mobile-logout-container"
            >
              <button className="mobile-logout-button" onClick={() => { handleLogout(); setMobileMenuOpen(false); }}>Logout</button>
            </motion.div>
          </motion.nav>
        )}
      </AnimatePresence>

      <div className="admin-icons admin-icons-desktop">
        <button className="logout-button" onClick={handleLogout}>Logout</button>
        {isSuperAdmin && (
          <Link to="/admin/super" className={`superadmin-link ${isActive('/admin/super') ? 'active' : ''}`}>Super Admin</Link>
        )}
      </div>
    </header>
    {createPortal(
      <div className="inventory-toast-stack" aria-live="polite" aria-atomic="true">
        {inventoryToasts.map(t => {
          const { payload } = t;
          const changeType = payload.type;
          let icon = '📦';
          let theme = '#175c36';
          if (changeType === 'added') { icon = '➕'; theme = '#2e7d32'; }
          else if (changeType === 'removed') { icon = '➖'; theme = '#b71c1c'; }
          else if (changeType === 'sold') { icon = '🛒'; theme = '#1565c0'; }
          return (
            <div key={t.id} className="inventory-toast" role="alert" style={{ borderLeftColor: theme }}>
              <div className="inventory-toast-icon" style={{ background: theme }}>{icon}</div>
              <div className="inventory-toast-content">
                <div className="inventory-toast-title">
                  {changeType === 'added' && 'Stock Added'}
                  {changeType === 'removed' && 'Stock Removed'}
                  {changeType === 'sold' && 'Stock Sold'}
                </div>
                <div className="inventory-toast-text">
                  {payload.productName} ({payload.delta > 0 ? '+' : ''}{payload.delta}) → {payload.newStock}
                </div>
              </div>
              <button className="inventory-toast-close" aria-label="Dismiss" onClick={() => setInventoryToasts(prev => prev.filter(x => x.id !== t.id))}>×</button>
            </div>
          );
        })}
      </div>, document.body)}
    </>
  );
}

export default AdminHeader;
